// src/gen/prng.ts
// Seeded RNG (§3.3, §8). The ONLY randomness source in gen/. No Math.random / Date.now anywhere.

/** Pure PRNG: float in [0,1). Deterministic given the seed. */
export type Rng = () => number;

/** Reference mulberry32. seed is coerced to uint32; identical sequence on every platform/run. */
export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Uniform integer in [0, maxExclusive). Precondition: maxExclusive >= 1. */
export function nextInt(rng: Rng, maxExclusive: number): number {
  return Math.floor(rng() * maxExclusive);
}

/** Uniform integer in [minInclusive, maxInclusive] (both inclusive). Precondition: min <= max. */
export function nextRange(rng: Rng, minInclusive: number, maxInclusive: number): number {
  const span = maxInclusive - minInclusive + 1;
  return minInclusive + Math.floor(rng() * span);
}

/** Uniform element. Precondition: items.length >= 1 (impl reads items[i]! behind that guard). */
export function pick<T>(rng: Rng, items: readonly T[]): T {
  const i = Math.floor(rng() * items.length);
  return items[i]!;
}

/** Fisher–Yates returning a NEW shuffled array (does not mutate input). */
export function shuffle<T>(rng: Rng, items: readonly T[]): T[] {
  const arr = items.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = arr[i]!; // i > 0 ⇒ in range
    arr[i] = arr[j]!; // j in [0, i] ⇒ in range
    arr[j] = tmp;
  }
  return arr;
}
